import { Tooltip } from '@mui/material';
import { DateTime } from 'luxon';
import { CSSProperties, MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '../../../../components/context-menu/context-menu';
import {
  DeleteConfirmationModal,
  deletePhaseText,
} from '../../../../components/modals/delete-confirmation-modal';
import { DisabledWithTooltip } from '../../../../components/permissions/DisabledWithTooltip';
import { cn } from '../../../../components/utils';
import { useViewer } from '../../../../context/viewer';
import { Phase, TimelineItem } from '../types';

// Minimal phase shape required to render a phase bar. Cross-team phases use
// this directly; current-team phases pass the full `Phase` (which extends it).
export type PhaseItemPhase = {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  color: string;
};

type PhaseItemBaseProps = TimelineItem;

type PhaseItemEditableProps = PhaseItemBaseProps & {
  readOnly?: false;
  phase: Phase;
  hideHoverButton: () => void;
  setPhaseIdToEdit: (phaseId: string) => void;
  deletePhase: (phaseId: string) => void;
};

type PhaseItemReadOnlyProps = PhaseItemBaseProps & {
  readOnly: true;
  phase: PhaseItemPhase;
  /**
   * Optional explicit positioning for the phase bar within its row. When
   * provided, overrides the default `inset-y-0 my-1` sizing so the bar can
   * keep the same height as a current-team phase bar even when the parent
   * row uses a different height (e.g. the 48px cross-team Phases row).
   */
  style?: CSSProperties;
};

type PhaseTimelineItemProps = PhaseItemEditableProps | PhaseItemReadOnlyProps;

export const PhaseItem = (props: PhaseTimelineItemProps) => {
  if (props.readOnly) {
    return <ReadOnlyPhase {...props} />;
  }
  return <EditablePhase {...props} />;
};

/**
 * Visual primitive — the colored phase bar with tooltip. Owns the calendar
 * positioning math and the className/style baseline so the read-only and
 * editable callers can't drift apart.
 */
function PhaseBar({
  phase,
  dateHeaderScale,
  timelineStart,
  style,
  onMouseEnter,
  onMouseMove,
}: {
  phase: PhaseItemPhase;
  dateHeaderScale: number;
  timelineStart: DateTime;
  style?: CSSProperties;
  onMouseEnter?: (event: MouseEvent<HTMLDivElement>) => void;
  onMouseMove?: (event: MouseEvent<HTMLDivElement>) => void;
}) {
  const startDatetime = DateTime.fromISO(phase.startDate);
  const left = startDatetime.diff(timelineStart).as('days') * dateHeaderScale;
  const width =
    DateTime.fromISO(phase.endDate).diff(startDatetime).as('days') *
    dateHeaderScale;

  // When the caller passes explicit `height`/`top`, drop `inset-y-0 my-1` so
  // the override actually sticks; otherwise fall back to the default (bar
  // fills the row minus 4px top/bottom margin).
  const hasExplicitVerticalSizing =
    style?.height !== undefined || style?.top !== undefined;

  return (
    <Tooltip disableInteractive placement="top" title={phase.name} arrow>
      <div
        className={cn(
          'absolute z-20 flex items-center justify-between p-2 text-sm border rounded-md',
          !hasExplicitVerticalSizing && 'inset-y-0 my-1',
        )}
        style={{
          left,
          width,
          backgroundColor: phase.color,
          ...style,
        }}
        onMouseEnter={onMouseEnter}
        onMouseMove={onMouseMove}
      >
        {phase.name}
      </div>
    </Tooltip>
  );
}

function ReadOnlyPhase(props: PhaseItemReadOnlyProps) {
  return <PhaseBar {...props} />;
}

const EditablePhase = ({
  phase,
  dateHeaderScale,
  timelineStart,
  hideHoverButton,
  setPhaseIdToEdit,
  deletePhase,
}: PhaseItemEditableProps) => {
  const { t } = useTranslation();
  const { hasPermissions } = useViewer();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const stopAndHide = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    hideHoverButton();
  };

  return (
    <>
      <ContextMenu modal={false}>
        <ContextMenuTrigger>
          <PhaseBar
            phase={phase}
            dateHeaderScale={dateHeaderScale}
            timelineStart={timelineStart}
            onMouseEnter={stopAndHide}
            onMouseMove={stopAndHide}
          />
        </ContextMenuTrigger>
        <ContextMenuContent>
          <DisabledWithTooltip
            disabled={!hasPermissions('plan.phases', 'edit')}
            reason={t('permissionDisabled.edit', { item: t('phases') })}
          >
            <ContextMenuItem onClick={() => setPhaseIdToEdit(phase.id)}>
              {t('editPhase')}
            </ContextMenuItem>
          </DisabledWithTooltip>
          <DisabledWithTooltip
            disabled={!hasPermissions('plan.phases', 'delete')}
            reason={t('permissionDisabled.delete', { item: t('phases') })}
          >
            <ContextMenuItem
              onClick={() => {
                setDeleteModalOpen(true);
              }}
            >
              {t('deletePhase')}
            </ContextMenuItem>
          </DisabledWithTooltip>
        </ContextMenuContent>
      </ContextMenu>
      <DeleteConfirmationModal
        id={phase.id}
        open={deleteModalOpen}
        {...deletePhaseText(t)}
        onClose={() => setDeleteModalOpen(false)}
        onDelete={() => deletePhase(phase.id)}
      />
    </>
  );
};
